import { Box, HStack, Icon, IconButton, Switch, Text, Tooltip, VStack } from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { TbBorderAll, TbBorderNone, TbInfoCircle } from 'react-icons/tb';
import React from 'react';
import ReconciliationDiagram from '../ReconciliationDiagram';
import { InvoiceCommentsSection } from './InvoiceCommentsSection';
import { InvoiceDetailFoldableSection } from './InvoiceDetailFoldableSection';
import { InvoiceKeyIdentifiersSection } from './InvoiceKeyIdentifiersSection';
import { InvoiceLineItemsSection } from './InvoiceLineItemsSection';
import { InvoiceReconciliationsTab } from './InvoiceReconciliationsTab';
import { InvoiceTotalsCard } from './InvoiceTotalsCard';
import type { InvoiceDetailResponse, InvoicePart, InvoiceType, ReconciliationMatchNote } from '../../types/invoice';
import type { ZoomImageHighlightBox } from '../ZoomImage';
import { fmtDollar } from '../../hooks/invoice/detailForm/invoiceDetailFormFormatters';
import { INVOICE_RECONCILIATION_SCROLL_ELEMENT_ID } from '../../utils/buildInvoiceReconciliationNextSteps';
import type { NexReconciliationIdentifierMode } from '../../utils/invoiceNexIdentifierUi';
import type { InvoiceMismatchFlagFamilyPolicy } from '../../utils/invoiceFlagFamilyPolicy';
import type { ExtractionLayoutField } from '../../hooks/invoice/useInvoiceExtractionLayout';
import type {
    KeyIdentifierCorrectionCommit,
    SubmittedIdentifierCorrectionRequest,
} from '../../utils/invoiceKeyIdentifierTeaching';

export type RhsTab = 'main' | 'diagram' | 'reconciliations';
const INVOICE_LINE_ITEMS_GRIDLINES_LS_KEY = 'invoice-line-items-gridlines-enabled';
const ACCENT = '#0071E3';

type LineItemField = keyof InvoiceDetailResponse['lineItems'][0];

export interface InvoiceRightPanelProps {
    panelWidthPercent?: number;
    invoice: InvoiceDetailResponse;
    editedFields: Partial<InvoiceDetailResponse>;
    activeRhsTab: RhsTab;
    hasReconciliationIssues: boolean;
    isEditingInvoiceNumber: boolean;
    setIsEditingInvoiceNumber: (v: boolean) => void;
    invoiceInputRef: React.RefObject<HTMLInputElement | null>;
    lineItemsTabIndex: number;
    setLineItemsTabIndex: (i: number) => void;
    lineItemsTotal: number;
    freightTotal: number;
    fuelItemsTotal: number;
    restockingFeeTotal: number;
    discountTotal: number;
    lineItemBackendIndexByCurrentIndex: (number | undefined)[];
    unmatchedPartIndices: Set<number>;
    localMatchedNexOverrides: Record<number, any>;
    flaggedPartIndices: Set<number>;
    expandedMatchedNexRows: Set<string>;
    setExpandedMatchedNexRows: React.Dispatch<React.SetStateAction<Set<string>>>;
    expandedMatchedNexIgnoredRows: Set<number>;
    setExpandedMatchedNexIgnoredRows: React.Dispatch<React.SetStateAction<Set<number>>>;
    setUnmatchConfirmModal: (v: { partIndex: number; partNumber?: string; nexPartNumber?: string; nexLine?: any } | null) => void;
    aboveLineItemsSubtotal: number;
    editingNumericField: string | null;
    editingNumericValues: Record<string, string>;
    setEditingNumericField: (v: string | null) => void;
    setEditingNumericValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    updateField: (field: keyof InvoiceDetailResponse, value: any) => void;
    updateLineItem: (index: number, field: LineItemField, value: any) => void;
    restoreLineItemRowsSnapshot: (snapshots: ReadonlyArray<{ row: number; item: InvoicePart }>) => void;
    addLineItem: () => void;
    addLineItemAbove: (index: number) => void;
    addLineItemAt: (index: number) => void;
    duplicateLineItemAt: (index: number) => void;
    removeLineItem: (index: number) => void;
    reorderLineItems: (fromIndex: number, toIndex: number) => void;
    reorderFreightItems: (fromIndex: number, toIndex: number) => void;
    reorderRestockingFeeItems: (fromIndex: number, toIndex: number) => void;
    reorderIgnoredParts: (fromIndex: number, toIndex: number) => void;
    addFreightItem: () => void;
    addFreightItemAt: (index: number) => void;
    addFreightItemAbove: (index: number) => void;
    duplicateFreightItemAt: (index: number) => void;
    removeFreightItem: (index: number) => void;
    updateFreightItem: (index: number, field: LineItemField, value: any) => void;
    addFuelItem: () => void;
    addFuelItemAt: (index: number) => void;
    removeFuelItem: (index: number) => void;
    updateFuelItem: (index: number, field: LineItemField, value: any) => void;
    addRestockingFeeItem: () => void;
    addRestockingFeeItemAt: (index: number) => void;
    addRestockingFeeItemAbove: (index: number) => void;
    duplicateRestockingFeeItemAt: (index: number) => void;
    removeRestockingFeeItem: (index: number) => void;
    updateRestockingFeeItem: (index: number, field: LineItemField, value: any) => void;
    restoreIgnoredPart: (index: number) => void;
    openRevertModal: (fieldKey: string, fieldLabel: string) => void;
    openReconciliationDetailModal: (modal: any) => void;
    getStringValue: (field: keyof InvoiceDetailResponse) => string;
    getDateValue: () => Date;
    getNumberValue: (field: keyof InvoiceDetailResponse) => number;
    isFieldEdited: (field: keyof InvoiceDetailResponse) => boolean;
    buildTabData: (label: string, count: number, total: number, showTotal?: boolean) => { label: string; badge: string; count: number };
    getLineItemStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    isLineItemFieldEdited: (index: number, field: LineItemField) => boolean;
    hasAnyLineItemFieldEdited: (index: number) => boolean;
    getLineItemEditedFieldLabels: (index: number) => string[];
    getIssuesForPart: (index: number) => any[];
    getLineItemExpandKey: (index: number, item: InvoiceDetailResponse['lineItems'][0]) => string;
    getIssueTooltip: (issues: any[], hasLocalMatch?: boolean) => React.ReactNode;
    getPartNumberChoiceIssue: (partIndex: number) => any;
    getMissingInNexSuggestion: (partIndex: number) => string | undefined;
    getFreightItemStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    getFreightItemPriceValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    getFuelItemStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    getFuelItemPriceValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    getRestockingFeeItemStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    getRestockingFeeItemPriceValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    renderLineItemNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    renderNumericField: (fieldName: keyof InvoiceDetailResponse, label?: string, fontWeight?: string, valueColor?: string) => React.ReactNode;
    getIssueTooltipText: (issue: any, hasLocalMatch?: boolean, asString?: boolean) => any;
    fmtUsd: (n: number) => string;
    renderFreightItemNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    renderFuelItemNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    renderRestockingFeeItemNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    addIgnoredPart: () => void;
    addIgnoredPartAt: (index: number) => void;
    addIgnoredPartAbove: (index: number) => void;
    removeIgnoredPart: (index: number) => void;
    updateIgnoredPart: (index: number, field: LineItemField, value: any) => void;
    getIgnoredPartStringValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    getIgnoredPartPriceValue: (index: number, field: LineItemField, defaultValue?: string) => string;
    renderIgnoredPartNumericCell: (index: number, field: 'listPrice' | 'unitBuyPrice' | 'totalBuyPrice', fw?: string) => React.ReactNode;
    effectiveMatchedNexLines?: Array<{ partIndex: number; nexLine: any }>;
    effectiveReconciliationMatchNotes?: ReconciliationMatchNote[];
    /** When true, all invoice fields are view-only. */
    isReadOnly: boolean;
    /** Editable invoices: request to change type (parent shows confirmation). */
    onInvoiceTypeChangeRequest?: (next: InvoiceType) => void;
    /** Editable invoices: request to change shop location (parent shows confirmation). */
    onShopLocationChangeRequest?: (next: { locationId: string; locationTitle: string }) => void;
    onRefreshNexsyisData?: (strategy?: 'po_first' | 'ro_only') => void;
    isRefreshNexsyisPending?: boolean;
    /** Live arithmetic from current form (before and after save). */
    localGrandTotalVsAboveLineItemsWithFeesMismatch?: {
        extractedTotal: number;
        expectedFromAboveLineItems: number;
    } | null;
    alwaysShowFreightTab?: boolean;
    freightAsLineItem?: boolean;
    salesTaxAsLineItem?: boolean;
    mismatchFlagFamilyPolicy?: InvoiceMismatchFlagFamilyPolicy;
    nexLineQuantityBasis?: import('../../utils/nexLineQuantity').NexLineQuantityBasis;
    /** Credited / return-slip: +$ discount, −$ taxes; received: −$ discount, +$ taxes; − before $ on credited order total. */
    creditedTotalsLayout?: boolean;
    /** Route param / invoice detail id — used to reset line-item fold state when opening a different invoice. */
    detailInvoiceId: string;
    /**
     * Key Identifiers open state from the invoice just viewed. Null on the first
     * invoice of this visit, which still follows reconciliation issues.
     */
    rememberedKeyIdentifiersOpen?: boolean | null;
    onRememberKeyIdentifiersOpen?: (open: boolean) => void;
    /** Hide OCR "Value changed" revert icons on line items after Match from NEX. */
    suppressLineItemValueChangedRevertUi?: boolean;
    /** Hover on a line item row highlights this region on the invoice image (image invoices only). */
    onLineItemImageHighlightChange?: (region: ZoomImageHighlightBox | null) => void;
    onFieldImageHighlightChange?: (region: ZoomImageHighlightBox | null) => void;
    nexReconciliationIdentifierMode?: NexReconciliationIdentifierMode;
    /** When 'split', show GST/HST + PST fields instead of a single Taxes field. */
    nexTaxFormat?: 'single' | 'split';
    /** Toggle split vs single tax format (Canadian invoices). */
    onNexTaxFormatChange?: (format: 'single' | 'split') => void;
    /** Show the split-tax toggle (Canadian locations or already split). */
    showCanadianTaxControls?: boolean;
    onOpenExtractionTeaching?: () => void;
    teachableFields?: ExtractionLayoutField[];
    onKeyIdentifierCorrectionCommitted?: (commit: KeyIdentifierCorrectionCommit) => void;
    /** Report a printed Key Identifier so it becomes an AI hint. */
    onReportSubmittedIdentifier?: (request: SubmittedIdentifierCorrectionRequest) => void;
}

export default function InvoiceRightPanel(props: InvoiceRightPanelProps) {
    const {
        invoice,
        editedFields,
        activeRhsTab,
        hasReconciliationIssues,
        isEditingInvoiceNumber,
        setIsEditingInvoiceNumber,
        invoiceInputRef,
        lineItemsTabIndex,
        setLineItemsTabIndex,
        lineItemsTotal,
        freightTotal,
        fuelItemsTotal,
        restockingFeeTotal,
        discountTotal,
        lineItemBackendIndexByCurrentIndex,
        unmatchedPartIndices,
        localMatchedNexOverrides,
        flaggedPartIndices,
        expandedMatchedNexRows,
        setExpandedMatchedNexRows,
        expandedMatchedNexIgnoredRows,
        setExpandedMatchedNexIgnoredRows,
        setUnmatchConfirmModal,
        aboveLineItemsSubtotal,
        setEditingNumericField,
        setEditingNumericValues,
        updateField,
        updateLineItem,
        restoreLineItemRowsSnapshot,
        addLineItem,
        addLineItemAbove,
        addLineItemAt,
        duplicateLineItemAt,
        removeLineItem,
        reorderLineItems,
        reorderFreightItems,
        reorderRestockingFeeItems,
        reorderIgnoredParts,
        addFreightItem,
        addFreightItemAt,
        addFreightItemAbove,
        duplicateFreightItemAt,
        removeFreightItem,
        updateFreightItem,
        addFuelItem,
        addFuelItemAt,
        removeFuelItem,
        updateFuelItem,
        addRestockingFeeItem,
        addRestockingFeeItemAt,
        addRestockingFeeItemAbove,
        duplicateRestockingFeeItemAt,
        removeRestockingFeeItem,
        updateRestockingFeeItem,
        restoreIgnoredPart,
        openRevertModal,
        openReconciliationDetailModal,
        getStringValue,
        getDateValue,
        getNumberValue,
        isFieldEdited,
        buildTabData,
        getLineItemStringValue,
        isLineItemFieldEdited,
        hasAnyLineItemFieldEdited,
        getLineItemEditedFieldLabels,
        getIssuesForPart,
        getLineItemExpandKey,
        getIssueTooltip,
        getPartNumberChoiceIssue,
        getMissingInNexSuggestion,
        getFreightItemStringValue,
        getFuelItemStringValue,
        getRestockingFeeItemStringValue,
        renderLineItemNumericCell,
        renderNumericField,
        getIssueTooltipText,
        fmtUsd,
        renderFreightItemNumericCell,
        renderFuelItemNumericCell,
        renderRestockingFeeItemNumericCell,
        addIgnoredPart,
        addIgnoredPartAt,
        addIgnoredPartAbove,
        removeIgnoredPart,
        updateIgnoredPart,
        getIgnoredPartStringValue,
        renderIgnoredPartNumericCell,
        effectiveMatchedNexLines,
        effectiveReconciliationMatchNotes,
        isReadOnly,
        onInvoiceTypeChangeRequest,
        onShopLocationChangeRequest,
        onRefreshNexsyisData,
        isRefreshNexsyisPending,
        localGrandTotalVsAboveLineItemsWithFeesMismatch,
        alwaysShowFreightTab,
        freightAsLineItem,
        salesTaxAsLineItem,
        mismatchFlagFamilyPolicy,
        nexLineQuantityBasis = 'qtyRec',
        creditedTotalsLayout,
        detailInvoiceId,
        rememberedKeyIdentifiersOpen = null,
        onRememberKeyIdentifiersOpen,
        suppressLineItemValueChangedRevertUi,
        onLineItemImageHighlightChange,
        onFieldImageHighlightChange,
        nexReconciliationIdentifierMode = 'ro',
        panelWidthPercent = 63,
        nexTaxFormat = 'single',
        onNexTaxFormatChange,
        showCanadianTaxControls = false,
        onOpenExtractionTeaching,
        teachableFields,
        onKeyIdentifierCorrectionCommitted,
        onReportSubmittedIdentifier,
    } = props;

    const fuelLineItemCount = (editedFields.fuelItems ?? invoice?.fuelItems ?? []).length;

    const additionalFeesInputWrap = (borderColor: string, bg: string) => ({
        w: '120px',
        border: '1px solid',
        borderColor,
        bg,
        borderRadius: 'md',
        _hover: { borderColor: 'gray.400' },
        _focusWithin: { borderColor: ACCENT, bg: 'blue.50' },
        px: 2,
        spacing: 0,
    });

    const nearlyZero = (n: number) => Math.abs(n) < 0.0001;
    const isFreightAsLineItem = freightAsLineItem ?? true;
    const freightHeaderForFees = getNumberValue('freightTotal');
    const fuelHeaderForFees = getNumberValue('fuelTotal');
    const isFuelAsLineItem = fuelLineItemCount > 0;
    /** Header freight/fuel only — never duplicate tab line totals in Additional Fees. */
    const freightDisplayForFees = isFreightAsLineItem ? 0 : freightHeaderForFees;
    const fuelDisplayForFees = isFuelAsLineItem ? 0 : fuelHeaderForFees;
    const showHeaderFuelInFees =
        Math.abs(fuelDisplayForFees) > 0.0001 || Math.abs(invoice?.fuelTotal ?? 0) > 0.0001;
    const taxesForFeesUi = getNumberValue('taxesTotal');
    const taxesMagnitudeForFees = Math.abs(taxesForFeesUi);
    const taxesNeutralForFees = nearlyZero(taxesMagnitudeForFees);
    const discountMagnitudeForFees = Math.abs(discountTotal ?? 0);
    const discountNeutralForFees = nearlyZero(discountMagnitudeForFees);
    const creditedFeesLayout = creditedTotalsLayout ?? false;
    const discountSignedForFees = getNumberValue('discountTotal');
    const SIGN_EPS = 0.0001;
    const taxesAmountNegativeForFees =
        !taxesNeutralForFees && (creditedFeesLayout || taxesForFeesUi < -SIGN_EPS);
    const discountAmountNegativeForFees = !discountNeutralForFees && discountSignedForFees < -SIGN_EPS;
    const taxesToneFees = taxesAmountNegativeForFees ? 'red.500' : 'black';
    const discountToneFees = discountAmountNegativeForFees ? 'red.500' : 'black';
    const aboveLineSubtotalTone = aboveLineItemsSubtotal < -SIGN_EPS ? 'red.500' : 'gray.900';
    const isSplitTaxFormat = nexTaxFormat === 'split';
    const pstForFeesUi = getNumberValue('pstTaxTotal');
    const pstMagnitudeForFees = Math.abs(pstForFeesUi);
    const pstNeutralForFees = nearlyZero(pstMagnitudeForFees);
    const pstAmountNegativeForFees =
        !pstNeutralForFees && (creditedFeesLayout || pstForFeesUi < -SIGN_EPS);
    const pstToneFees = pstAmountNegativeForFees ? 'red.500' : 'black';

    const renderTaxAmountRow = (label: string, field: 'taxesTotal' | 'pstTaxTotal', tone: string) => (
        <HStack
            justify="space-between"
            align="center"
            onMouseEnter={() =>
                field === 'taxesTotal'
                    ? onFieldImageHighlightChange?.(invoice.imageFieldHighlights?.taxesTotal ?? null)
                    : onFieldImageHighlightChange?.(null)
            }
            onMouseLeave={() => onFieldImageHighlightChange?.(null)}
        >
            <Text fontSize="sm" color="gray.600">{label}</Text>
            <HStack spacing={1}>
                <HStack {...additionalFeesInputWrap('gray.200', 'transparent')}>
                    {creditedFeesLayout && field === 'taxesTotal' && !taxesNeutralForFees && (
                        <Text fontSize="md" color={tone} flexShrink={0} userSelect="none">-</Text>
                    )}
                    {creditedFeesLayout && field === 'pstTaxTotal' && !pstNeutralForFees && (
                        <Text fontSize="md" color={tone} flexShrink={0} userSelect="none">-</Text>
                    )}
                    {!creditedFeesLayout && field === 'taxesTotal' && !taxesNeutralForFees && (
                        <Text fontSize="md" color={tone} flexShrink={0} userSelect="none">+</Text>
                    )}
                    {!creditedFeesLayout && field === 'pstTaxTotal' && !pstNeutralForFees && (
                        <Text fontSize="md" color={tone} flexShrink={0} userSelect="none">+</Text>
                    )}
                    <Text fontSize="md" color={tone} flexShrink={0}>$</Text>
                    <Box flex="1" textAlign="right">
                        {renderNumericField(field, undefined, undefined, tone)}
                    </Box>
                </HStack>
            </HStack>
        </HStack>
    );

    const [keyIdentifiersOpen, setKeyIdentifiersOpen] = React.useState(
        rememberedKeyIdentifiersOpen ?? hasReconciliationIssues,
    );
    const [lineItemsOpen, setLineItemsOpen] = React.useState(true);
    const [additionalFeesOpen, setAdditionalFeesOpen] = React.useState(true);
    const [showTableGridlines, setShowTableGridlines] = React.useState(true);
    const prevDetailInvoiceIdRef = React.useRef<string | undefined>(undefined);

    const rememberKeyIdentifiersOpen = (open: boolean) => {
        setKeyIdentifiersOpen(open);
        onRememberKeyIdentifiersOpen?.(open);
    };

    React.useLayoutEffect(() => {
        onRememberKeyIdentifiersOpen?.(keyIdentifiersOpen);
        // Publish the first invoice's open state once, including the reconciliation default,
        // so the next invoice keeps it even if this panel remounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useLayoutEffect(() => {
        if (detailInvoiceId !== prevDetailInvoiceIdRef.current) {
            prevDetailInvoiceIdRef.current = detailInvoiceId;
            setLineItemsOpen(true);
            setAdditionalFeesOpen(true);
        }
    }, [detailInvoiceId]);

    React.useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            const stored = window.localStorage.getItem(INVOICE_LINE_ITEMS_GRIDLINES_LS_KEY);
            if (stored === '0') setShowTableGridlines(false);
            else if (stored === '1') setShowTableGridlines(true);
        } catch {
            // Ignore storage errors; default to gridlines on.
        }
    }, []);

    React.useEffect(() => {
        if (typeof window === 'undefined') return;
        try {
            window.localStorage.setItem(INVOICE_LINE_ITEMS_GRIDLINES_LS_KEY, showTableGridlines ? '1' : '0');
        } catch {
            // Ignore storage errors.
        }
    }, [showTableGridlines]);

    return (
        <Box
            w={`${panelWidthPercent}%`}
            h="100%"
            overflowY="auto"
            overflowX="hidden"
            px={6}
            py={5}
        >
      {/* ════════════════════════ MAIN TAB ════════════════════════ */}
      {activeRhsTab === 'main' && (
          <VStack spacing={6} align="stretch">

              {/* ── Key Identifiers ─────────────────────────── */}
              <InvoiceDetailFoldableSection
                  title="Key Identifiers"
                  isOpen={keyIdentifiersOpen}
                  onToggle={rememberKeyIdentifiersOpen}
                  contentPadding={0}
                  sectionId={INVOICE_RECONCILIATION_SCROLL_ELEMENT_ID['key-identifiers']}
              >
                  <InvoiceKeyIdentifiersSection
                      invoice={invoice}
                      editedFields={editedFields}
                      isEditingInvoiceNumber={isEditingInvoiceNumber}
                      setIsEditingInvoiceNumber={setIsEditingInvoiceNumber}
                      invoiceInputRef={invoiceInputRef}
                      getStringValue={getStringValue}
                      getDateValue={getDateValue}
                      isFieldEdited={isFieldEdited}
                      updateField={updateField}
                      openRevertModal={openRevertModal}
                      openReconciliationDetailModal={openReconciliationDetailModal}
                      isReadOnly={isReadOnly}
                      onInvoiceTypeChangeRequest={onInvoiceTypeChangeRequest}
                      onShopLocationChangeRequest={onShopLocationChangeRequest}
                      onRefreshNexsyisData={onRefreshNexsyisData}
                      isRefreshNexsyisPending={isRefreshNexsyisPending}
                      onFieldImageHighlightChange={onFieldImageHighlightChange}
                      hideTitle
                      nexReconciliationIdentifierMode={nexReconciliationIdentifierMode}
                      onOpenExtractionTeaching={onOpenExtractionTeaching}
                      teachableFields={teachableFields}
                      onKeyIdentifierCorrectionCommitted={onKeyIdentifierCorrectionCommitted}
                      onReportSubmittedIdentifier={onReportSubmittedIdentifier}
                  />
              </InvoiceDetailFoldableSection>

              {/* ── Line Items ──────────────────────────────── */}
              <InvoiceDetailFoldableSection
                  title="Line Items"
                  isOpen={lineItemsOpen}
                  onToggle={setLineItemsOpen}
                  contentPadding={0}
                  allowOverflow
                  sectionId={INVOICE_RECONCILIATION_SCROLL_ELEMENT_ID['line-items']}
                  titleAccessory={
                      <HStack spacing={2} align="center">
                          {!isReadOnly ? (
                              <Tooltip
                                  label="Tab, Enter, and arrows move between line cells. Type to replace a value, or click to edit. ⌘/Ctrl+click to select cells; ⌘C / ⌘V paste; ⌘Z / Ctrl+Z undoes the last paste."
                                  hasArrow
                                  placement="top"
                                  openDelay={200}
                              >
                                  <IconButton
                                      aria-label="Line items table keyboard shortcuts"
                                      icon={<Icon as={TbInfoCircle as React.ElementType} boxSize={3.5} />}
                                      variant="ghost"
                                      size="xs"
                                      minW="auto"
                                      h="auto"
                                      p={0.5}
                                      color="gray.400"
                                      _hover={{ color: 'gray.600', bg: 'gray.100' }}
                                  />
                              </Tooltip>
                          ) : null}
                          <HStack
                              spacing={0}
                              role="radiogroup"
                              aria-label="Table gridlines"
                              bg="gray.100"
                              borderRadius="full"
                              p={0.5}
                              border="1px solid"
                              borderColor="gray.200"
                          >
                              <Tooltip label="Gridlines on" hasArrow placement="top">
                                  <IconButton
                                      aria-label="Gridlines on"
                                      icon={<Icon as={TbBorderAll as React.ElementType} boxSize={3.5} />}
                                      size="xs"
                                      h="28px"
                                      minW="28px"
                                      w="28px"
                                      borderRadius="full"
                                      bg={showTableGridlines ? 'white' : 'transparent'}
                                      color={showTableGridlines ? 'gray.900' : 'gray.500'}
                                      boxShadow={showTableGridlines ? 'sm' : 'none'}
                                      _hover={{
                                          bg: showTableGridlines ? 'white' : 'gray.200',
                                          color: showTableGridlines ? 'gray.900' : 'gray.700',
                                      }}
                                      onClick={() => setShowTableGridlines(true)}
                                  />
                              </Tooltip>
                              <Tooltip label="Gridlines off" hasArrow placement="top">
                                  <IconButton
                                      aria-label="Gridlines off"
                                      icon={<Icon as={TbBorderNone as React.ElementType} boxSize={3.5} />}
                                      size="xs"
                                      h="28px"
                                      minW="28px"
                                      w="28px"
                                      borderRadius="full"
                                      bg={!showTableGridlines ? 'white' : 'transparent'}
                                      color={!showTableGridlines ? 'gray.900' : 'gray.500'}
                                      boxShadow={!showTableGridlines ? 'sm' : 'none'}
                                      _hover={{
                                          bg: !showTableGridlines ? 'white' : 'gray.200',
                                          color: !showTableGridlines ? 'gray.900' : 'gray.700',
                                      }}
                                      onClick={() => setShowTableGridlines(false)}
                                  />
                              </Tooltip>
                          </HStack>
                      </HStack>
                  }
                  headerRight={
                      !lineItemsOpen ? (
                          <Text fontSize="sm" fontWeight="bold" color={aboveLineSubtotalTone}>
                              Subtotal: {fmtDollar(aboveLineItemsSubtotal)}
                          </Text>
                      ) : null
                  }
              >
              <InvoiceLineItemsSection
                  invoice={invoice}
                  editedFields={editedFields}
                  lineItemsTabIndex={lineItemsTabIndex}
                  setLineItemsTabIndex={setLineItemsTabIndex}
                  lineItemsTotal={lineItemsTotal}
                  freightTotal={freightTotal}
                  fuelItemsTotal={fuelItemsTotal}
                  restockingFeeTotal={restockingFeeTotal}
                  discountTotal={discountTotal}
                  lineItemBackendIndexByCurrentIndex={lineItemBackendIndexByCurrentIndex}
                  unmatchedPartIndices={unmatchedPartIndices}
                  localMatchedNexOverrides={localMatchedNexOverrides}
                  flaggedPartIndices={flaggedPartIndices}
                  expandedMatchedNexRows={expandedMatchedNexRows}
                  setExpandedMatchedNexRows={setExpandedMatchedNexRows}
                  expandedMatchedNexIgnoredRows={expandedMatchedNexIgnoredRows}
                  setExpandedMatchedNexIgnoredRows={setExpandedMatchedNexIgnoredRows}
                  setUnmatchConfirmModal={setUnmatchConfirmModal}
                  aboveLineItemsSubtotal={aboveLineItemsSubtotal}
                  buildTabData={buildTabData}
                  updateLineItem={updateLineItem}
                  restoreLineItemRowsSnapshot={restoreLineItemRowsSnapshot}
                  addLineItem={addLineItem}
                  addLineItemAbove={addLineItemAbove}
                  addLineItemAt={addLineItemAt}
                  duplicateLineItemAt={duplicateLineItemAt}
                  removeLineItem={removeLineItem}
                  reorderLineItems={reorderLineItems}
                  reorderFreightItems={reorderFreightItems}
                  reorderRestockingFeeItems={reorderRestockingFeeItems}
                  reorderIgnoredParts={reorderIgnoredParts}
                  addFreightItem={addFreightItem}
                  addFreightItemAt={addFreightItemAt}
                  addFreightItemAbove={addFreightItemAbove}
                  duplicateFreightItemAt={duplicateFreightItemAt}
                  removeFreightItem={removeFreightItem}
                  updateFreightItem={updateFreightItem}
                  addFuelItem={addFuelItem}
                  addFuelItemAt={addFuelItemAt}
                  removeFuelItem={removeFuelItem}
                  updateFuelItem={updateFuelItem}
                  addRestockingFeeItem={addRestockingFeeItem}
                  addRestockingFeeItemAt={addRestockingFeeItemAt}
                  addRestockingFeeItemAbove={addRestockingFeeItemAbove}
                  duplicateRestockingFeeItemAt={duplicateRestockingFeeItemAt}
                  removeRestockingFeeItem={removeRestockingFeeItem}
                  updateRestockingFeeItem={updateRestockingFeeItem}
                  restoreIgnoredPart={restoreIgnoredPart}
                  openRevertModal={openRevertModal}
                  openReconciliationDetailModal={openReconciliationDetailModal}
                  getLineItemStringValue={getLineItemStringValue}
                  setEditingNumericField={setEditingNumericField}
                  setEditingNumericValues={setEditingNumericValues}
                  isLineItemFieldEdited={isLineItemFieldEdited}
                  hasAnyLineItemFieldEdited={hasAnyLineItemFieldEdited}
                  getLineItemEditedFieldLabels={getLineItemEditedFieldLabels}
                  getIssuesForPart={getIssuesForPart}
                  getLineItemExpandKey={getLineItemExpandKey}
                  getIssueTooltip={getIssueTooltip}
                  getPartNumberChoiceIssue={getPartNumberChoiceIssue}
                  getMissingInNexSuggestion={getMissingInNexSuggestion}
                  getFreightItemStringValue={getFreightItemStringValue}
                  getFuelItemStringValue={getFuelItemStringValue}
                  getRestockingFeeItemStringValue={getRestockingFeeItemStringValue}
                  renderLineItemNumericCell={renderLineItemNumericCell}
                  renderFreightItemNumericCell={renderFreightItemNumericCell}
                  renderFuelItemNumericCell={renderFuelItemNumericCell}
                  renderRestockingFeeItemNumericCell={renderRestockingFeeItemNumericCell}
                  fmtUsd={fmtUsd}
                  addIgnoredPart={addIgnoredPart}
                  addIgnoredPartAt={addIgnoredPartAt}
                  addIgnoredPartAbove={addIgnoredPartAbove}
                  removeIgnoredPart={removeIgnoredPart}
                  updateIgnoredPart={updateIgnoredPart}
                  getIgnoredPartStringValue={getIgnoredPartStringValue}
                  renderIgnoredPartNumericCell={renderIgnoredPartNumericCell}
                  effectiveMatchedNexLines={effectiveMatchedNexLines}
                  effectiveReconciliationMatchNotes={effectiveReconciliationMatchNotes}
                  isReadOnly={isReadOnly}
                  alwaysShowFreightTab={alwaysShowFreightTab}
                  hideTitle
                  suppressLineItemValueChangedRevertUi={suppressLineItemValueChangedRevertUi}
                  onLineItemImageHighlightChange={onLineItemImageHighlightChange}
                  showTableGridlines={showTableGridlines}
                  nexLineQuantityBasis={nexLineQuantityBasis}
              />
              <Box borderTop="1px solid" borderColor="gray.200">
                  <HStack justify="flex-end" align="center" px={3} py={3} spacing={2}>
                      <Text fontSize="sm" color="gray.700" fontWeight="bold">
                          Above Line Items Subtotal
                      </Text>
                      <Text fontSize="lg" color={aboveLineSubtotalTone} fontWeight="bold" px={1}>
                          {fmtDollar(aboveLineItemsSubtotal)}
                      </Text>
                  </HStack>
              </Box>
          </InvoiceDetailFoldableSection>

              <InvoiceDetailFoldableSection
                  title="Additional Fees"
                  isOpen={additionalFeesOpen}
                  onToggle={setAdditionalFeesOpen}
                  sectionId={INVOICE_RECONCILIATION_SCROLL_ELEMENT_ID['additional-fees']}
              >
                  <Box p={1}>
                      <VStack spacing={2} align="stretch" w="100%">
                          {showCanadianTaxControls && !isReadOnly && onNexTaxFormatChange && (
                              <HStack justify="space-between" align="center">
                                  <HStack spacing={1} align="center">
                                      <Text fontSize="sm" color="gray.600" fontWeight="bold">
                                          Split GST/HST + PST
                                      </Text>
                                      <Tooltip
                                          label="Turn on when the vendor invoice lists GST/HST and PST as separate lines in the totals (PST can be $0). Yaxle sends those amounts to Nexsyis as GST/HST Tax and PST Tax. Leave off when the invoice shows one generic Tax or Sales Tax line. Default GST/HST and PST rates for Canadian locations are configured under Settings → Invoicing → Default tax rate by location."
                                          placement="top"
                                          hasArrow
                                          maxW="340px"
                                      >
                                          <Box as="span" display="inline-flex" lineHeight={0}>
                                              <Icon
                                                  as={InfoOutlineIcon}
                                                  boxSize={3.5}
                                                  color="gray.400"
                                                  cursor="help"
                                              />
                                          </Box>
                                      </Tooltip>
                                  </HStack>
                                  <Switch
                                      size="sm"
                                      colorScheme="blue"
                                      isChecked={isSplitTaxFormat}
                                      onChange={(e) => {
                                          const next = e.target.checked ? 'split' : 'single';
                                          onNexTaxFormatChange(next);
                                          if (next === 'split' && getNumberValue('pstTaxTotal') === 0 && !isFieldEdited('pstTaxTotal')) {
                                              updateField('pstTaxTotal', 0);
                                          }
                                      }}
                                  />
                              </HStack>
                          )}
                          {!isFreightAsLineItem && (
                              <HStack justify="space-between" align="center">
                                  <Text fontSize="sm" color="gray.600">Freight</Text>
                                  <HStack spacing={1}>
                                      <HStack {...additionalFeesInputWrap('gray.200', 'transparent')}>
                                          <Text fontSize="md" color="black" flexShrink={0}>$</Text>
                                          <Box flex="1" textAlign="right">
                                              {isReadOnly
                                                  ? fmtUsd(freightDisplayForFees)
                                                  : renderNumericField('freightTotal')}
                                          </Box>
                                      </HStack>
                                  </HStack>
                              </HStack>
                          )}
                          {showHeaderFuelInFees && (
                              <HStack justify="space-between" align="center">
                                  <Text fontSize="sm" color="gray.600">Fuel</Text>
                                  <HStack spacing={1}>
                                      <HStack {...additionalFeesInputWrap('gray.200', 'transparent')}>
                                          <Text fontSize="md" color="black" flexShrink={0}>$</Text>
                                          <Box flex="1" textAlign="right">
                                              {isReadOnly || isFuelAsLineItem
                                                  ? fmtUsd(fuelDisplayForFees)
                                                  : renderNumericField('fuelTotal')}
                                          </Box>
                                      </HStack>
                                  </HStack>
                              </HStack>
                          )}
                          <HStack
                              justify="space-between"
                              align="center"
                              onMouseEnter={() => onFieldImageHighlightChange?.(invoice.imageFieldHighlights?.discountTotal ?? null)}
                              onMouseLeave={() => onFieldImageHighlightChange?.(null)}
                          >
                              <Text fontSize="sm" color="gray.600">Discount</Text>
                              <HStack spacing={1}>
                                  <HStack {...additionalFeesInputWrap('gray.200', 'transparent')}>
                                      {!discountNeutralForFees && (
                                          <>
                                              <Text fontSize="md" color={discountToneFees} flexShrink={0} userSelect="none">
                                                  {creditedFeesLayout ? '+' : '-'}
                                              </Text>
                                              <Text fontSize="md" color={discountToneFees} flexShrink={0}>$</Text>
                                          </>
                                      )}
                                      {discountNeutralForFees && (
                                          <Text fontSize="md" color={discountToneFees} flexShrink={0}>$</Text>
                                      )}
                                      <Box flex="1" textAlign="right">
                                          {renderNumericField('discountTotal', undefined, undefined, discountToneFees)}
                                      </Box>
                                  </HStack>
                              </HStack>
                          </HStack>
                          {isSplitTaxFormat ? (
                              <>
                                  {renderTaxAmountRow('GST/HST', 'taxesTotal', taxesToneFees)}
                                  {renderTaxAmountRow('PST', 'pstTaxTotal', pstToneFees)}
                              </>
                          ) : (
                              !(salesTaxAsLineItem ?? false) &&
                              renderTaxAmountRow('Taxes', 'taxesTotal', taxesToneFees)
                          )}
                      </VStack>
                  </Box>
              </InvoiceDetailFoldableSection>

              <Box id={INVOICE_RECONCILIATION_SCROLL_ELEMENT_ID.totals}>
                  <InvoiceTotalsCard
                      invoice={invoice}
                      getNumberValue={getNumberValue}
                      isFieldEdited={isFieldEdited}
                      renderNumericField={renderNumericField}
                      openRevertModal={openRevertModal}
                      fmtUsd={fmtUsd}
                      isReadOnly={isReadOnly}
                      localGrandTotalVsAboveLineItemsWithFeesMismatch={localGrandTotalVsAboveLineItemsWithFeesMismatch}
                      freightAsLineItem={freightAsLineItem ?? true}
                      fuelLineItemCount={fuelLineItemCount}
                      discountTotal={discountTotal}
                      creditedTotalsLayout={creditedTotalsLayout}
                      onFieldImageHighlightChange={onFieldImageHighlightChange}
                      hideAdditionalFees
                      hideTitle
                      totalOnly
                  />
              </Box>

              <InvoiceCommentsSection invoiceId={detailInvoiceId} />
          </VStack>
      )}

      {/* ═══════════════════════════ DIAGRAM TAB ═══════════════════════════ */}
      {activeRhsTab === 'diagram' && (
          <ReconciliationDiagram
              lineItems={editedFields.lineItems ?? invoice?.lineItems ?? []}
              originalExtractedLineItems={invoice?.originalExtracted?.lineItems}
              lineItemCorrectionMetadata={invoice?.lineItemCorrectionMetadata}
              reconciliationIssues={invoice?.reconciliationIssues}
              invoiceType={invoice?.invoiceType}
              nexLineQuantityBasis={nexLineQuantityBasis}
          />
      )}

      {/* ════════════════════ KEY RECONCILIATIONS TAB ════════════════════ */}
      {activeRhsTab === 'reconciliations' && (
          <Box id={INVOICE_RECONCILIATION_SCROLL_ELEMENT_ID.reconciliations}>
              <InvoiceReconciliationsTab
                  invoice={invoice}
                  editedFields={editedFields}
                  getStringValue={getStringValue}
                  getIssueTooltipText={getIssueTooltipText}
                  fmtUsd={fmtUsd}
                  freightAsLineItem={freightAsLineItem ?? true}
                  salesTaxAsLineItem={salesTaxAsLineItem ?? false}
                  openReconciliationDetailModal={openReconciliationDetailModal}
                  effectiveReconciliationMatchNotes={effectiveReconciliationMatchNotes}
                  liveGrandTotalVsAboveLineItemsWithFeesMismatch={localGrandTotalVsAboveLineItemsWithFeesMismatch}
                  nexReconciliationIdentifierMode={nexReconciliationIdentifierMode}
                  mismatchFlagFamilyPolicy={mismatchFlagFamilyPolicy}
              />
          </Box>
      )}
        </Box>
    );
}
